<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'move',
    validator: value => ['move', 'add'].includes(value),
  },
  listName: { type: String, default: '' },
  favorites: { type: Array, default: () => [] },
  availableLists: { type: Array, default: () => [] },
  preferredListName: { type: String, default: '' },
  preselectedFavoriteIds: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);
const MIN_LIST_NAME_LENGTH = 3;
const selectedFavoriteIds = ref([]);
const sortMode = ref('recent');
const targetMode = ref('existing');
const selectedListName = ref('');
const newListName = ref('');

const isAddMode = computed(() => props.mode === 'add');

const selectableLists = computed(() =>
  props.availableLists.filter(
    option => isAddMode.value || option.value !== props.listName
  )
);

const preferredExistingList = computed(() => {
  const preferredName = props.preferredListName.trim();
  if (!preferredName) return null;

  return selectableLists.value.find(
    option =>
      option.value?.toString().localeCompare(preferredName, 'pt-BR', {
        sensitivity: 'accent',
      }) === 0
  );
});

const dialogTitle = computed(() =>
  isAddMode.value ? 'Adicionar imóveis à lista' : 'Salvar em uma lista'
);

const dialogDescription = computed(() =>
  isAddMode.value
    ? 'Selecione os imóveis e escolha a lista de destino para salvar tudo de uma vez.'
    : 'Selecione os imóveis e escolha a lista de destino para manter seus favoritos organizados.'
);

const existingListHelper = computed(() =>
  isAddMode.value
    ? 'Listas vazias também aparecem aqui; duplicados serão ignorados ao salvar.'
    : 'Somente listas diferentes da atual aparecem aqui.'
);

const selectedItemsLabel = computed(() => {
  const count = selectedFavoriteIds.value.length;
  const noun = count === 1 ? 'imóvel selecionado' : 'imóveis selecionados';
  return `${count} ${noun}.`;
});

const submitLabel = computed(() => (isAddMode.value ? 'Adicionar' : 'Salvar'));

watch(
  () => props.show,
  value => {
    if (value) {
      const availableIds = props.favorites.map(favorite => favorite.id);
      const preselectedIds = props.preselectedFavoriteIds.filter(id =>
        availableIds.includes(id)
      );
      selectedFavoriteIds.value = preselectedIds.length
        ? preselectedIds
        : availableIds;
      selectedListName.value =
        preferredExistingList.value?.value ||
        selectableLists.value[0]?.value ||
        '';
      newListName.value =
        selectableLists.value.length || !props.preferredListName
          ? ''
          : props.preferredListName;
      sortMode.value = 'recent';
      targetMode.value = selectableLists.value.length ? 'existing' : 'new';
    }
  }
);

const favoritePrice = favorite => {
  const imovel = favorite?.imovel || {};
  const rawValue =
    imovel.finalidade === 'aluguel' ? imovel.valor_aluguel : imovel.valor_venda;
  return Number(rawValue || 0);
};

const sortedFavorites = computed(() => {
  const items = [...props.favorites];

  if (sortMode.value === 'price_desc') {
    return items.sort(
      (left, right) => favoritePrice(right) - favoritePrice(left)
    );
  }

  if (sortMode.value === 'price_asc') {
    return items.sort(
      (left, right) => favoritePrice(left) - favoritePrice(right)
    );
  }

  if (sortMode.value === 'bairro') {
    return items.sort((left, right) =>
      `${left.imovel?.bairro || ''}`.localeCompare(
        `${right.imovel?.bairro || ''}`,
        'pt-BR'
      )
    );
  }

  return items.sort(
    (left, right) =>
      new Date(right.favorited_at || 0) - new Date(left.favorited_at || 0)
  );
});

const targetListName = computed(() =>
  targetMode.value === 'new' ? newListName.value.trim() : selectedListName.value
);
const newListNameLength = computed(() => newListName.value.trim().length);
const isNewListNameTooShort = computed(
  () =>
    newListNameLength.value > 0 &&
    newListNameLength.value < MIN_LIST_NAME_LENGTH
);
const isNewListNameInvalid = computed(
  () => isNewListNameTooShort.value || newListNameLength.value > 40
);

const canSubmit = computed(() => {
  if (props.isLoading || !selectedFavoriteIds.value.length) return false;

  if (targetMode.value === 'new') {
    return (
      newListNameLength.value >= MIN_LIST_NAME_LENGTH &&
      newListNameLength.value <= 40
    );
  }

  return Boolean(targetListName.value);
});

const isSelected = favoriteId => selectedFavoriteIds.value.includes(favoriteId);

const toggleFavorite = favoriteId => {
  if (isSelected(favoriteId)) {
    selectedFavoriteIds.value = selectedFavoriteIds.value.filter(
      id => id !== favoriteId
    );
    return;
  }

  selectedFavoriteIds.value = [...selectedFavoriteIds.value, favoriteId];
};

const formatCurrency = value => {
  if (!value) return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const submit = () => {
  if (!canSubmit.value) return;

  const selectedIds = [...selectedFavoriteIds.value];
  const payload = {
    favoriteIds: selectedIds,
    targetListName: targetListName.value,
    sortMode: sortMode.value,
    mode: props.mode,
  };

  if (isAddMode.value) {
    payload.imovelIds = selectedIds;
  }

  emit('submit', payload);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    :title="dialogTitle"
    max-width-class="max-w-[760px]"
    @close="emit('close')"
  >
    <div class="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_280px]">
      <div class="space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-[13px] leading-6 text-lais-text-muted">
            {{ dialogDescription }}
          </p>

          <select
            v-model="sortMode"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option value="recent">Mais recentes</option>
            <option value="price_desc">Maior preço</option>
            <option value="price_asc">Menor preço</option>
            <option value="bairro">Bairro</option>
          </select>
        </div>

        <div
          v-if="sortedFavorites.length === 0"
          class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-6 text-[13px] leading-6 text-lais-text-muted"
        >
          Nenhum imóvel selecionado para esta ação.
        </div>

        <div v-else class="grid gap-3 max-h-[420px] overflow-auto pr-1">
          <label
            v-for="favorite in sortedFavorites"
            :key="favorite.id"
            class="flex cursor-pointer items-start gap-3 rounded-lais border border-lais-border bg-white px-4 py-4 transition-all hover:border-lais-primary"
          >
            <input
              :checked="isSelected(favorite.id)"
              type="checkbox"
              class="mt-1 size-4 accent-[#49317a]"
              @change="toggleFavorite(favorite.id)"
            />

            <div class="min-w-0 flex-1">
              <p class="text-[14px] font-semibold text-lais-text">
                {{ favorite.imovel?.titulo || `Imóvel #${favorite.imovel_id}` }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{
                  [
                    favorite.imovel?.bairro,
                    favorite.imovel?.cidade,
                    favorite.imovel?.tipo,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                }}
              </p>
              <p
                v-if="favoritePrice(favorite)"
                class="mt-2 text-[12px] font-semibold text-lais-text"
              >
                {{ formatCurrency(favoritePrice(favorite)) }}
              </p>
            </div>
          </label>
        </div>
      </div>

      <div
        class="space-y-4 rounded-lais border border-lais-border bg-[#faf8fd] p-4"
      >
        <div class="space-y-2">
          <p class="text-[14px] font-semibold text-lais-text">
            Salvar em uma lista
          </p>
          <p class="text-[12px] leading-5 text-lais-text-muted">
            Escolha uma lista existente ou crie uma nova coleção.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all"
            :class="
              targetMode === 'existing'
                ? 'bg-lais-primary text-white'
                : 'border border-lais-border bg-white text-lais-text'
            "
            :disabled="!selectableLists.length"
            @click="targetMode = 'existing'"
          >
            Usar lista existente
          </button>
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all"
            :class="
              targetMode === 'new'
                ? 'bg-lais-primary text-white'
                : 'border border-lais-border bg-white text-lais-text'
            "
            @click="targetMode = 'new'"
          >
            Criar nova lista
          </button>
        </div>

        <LaisField
          v-if="targetMode === 'existing'"
          label="Lista de destino"
          :helper="existingListHelper"
        >
          <LaisSelect
            v-model="selectedListName"
            :options="selectableLists"
            placeholder="Escolha a lista"
          />
        </LaisField>

        <LaisField v-else label="Nome da lista">
          <input
            v-model="newListName"
            type="text"
            maxlength="40"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-[#efe8fb]"
            placeholder="Exemplo: casas no centro"
          />
        </LaisField>

        <div v-if="targetMode === 'new'" class="space-y-2">
          <p
            v-if="isNewListNameTooShort"
            class="text-[12px] font-semibold text-[#b42318]"
          >
            O nome precisa ter ao menos 3 caracteres.
          </p>
          <div class="flex items-center justify-between gap-3 text-[12px]">
            <span class="text-lais-text-muted"> Nome da lista </span>
            <span
              class="font-semibold"
              :class="
                isNewListNameInvalid ? 'text-[#b42318]' : 'text-lais-text-muted'
              "
            >
              {{ newListNameLength }}/40
            </span>
          </div>
        </div>

        <div
          class="rounded-lais border border-dashed border-lais-border bg-white px-3 py-3 text-[12px] leading-5 text-lais-text-muted"
        >
          {{ selectedItemsLabel }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Cancelar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-arrow-right-left"
          :is-loading="isLoading"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitLabel }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
